import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { ProcessArtefakt, GenerateProcessResponse } from '../types/process';
import ProcessViewer from './ProcessViewer';
import ProcessList from './ProcessList';
import './ProcessGenerator.css';

const MIN_TEXT_LENGTH = 10;
const MAX_TEXT_LENGTH = 15000;

const ProcessGenerator: React.FC = () => {
  const [text, setText] = useState('');
  const [titel, setTitel] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [artefakte, setArtefakte] = useState<ProcessArtefakt[]>([]);
  const [currentArtefakt, setCurrentArtefakt] = useState<ProcessArtefakt | null>(null);
  const [showList, setShowList] = useState(false);

  // Load saved processes from localStorage
  useEffect(() => {
    const saved = localStorage.getItem('processArtefakte');
    if (saved) {
      try {
        setArtefakte(JSON.parse(saved));
      } catch (e) {
        console.error('Failed to load saved processes:', e);
      }
    }
  }, []);

  // Save processes to localStorage
  useEffect(() => {
    if (artefakte.length > 0) {
      localStorage.setItem('processArtefakte', JSON.stringify(artefakte));
    }
  }, [artefakte]);

  const handleGenerate = async () => {
    if (!text || text.length < MIN_TEXT_LENGTH) {
      setError(`Text muss mindestens ${MIN_TEXT_LENGTH} Zeichen lang sein.`);
      return;
    }

    if (text.length > MAX_TEXT_LENGTH) {
      setError(`Text darf maximal ${MAX_TEXT_LENGTH} Zeichen lang sein.`);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Use relative URL if in production, fallback to localhost for development
      const apiUrl = process.env.REACT_APP_API_URL || (
        window.location.hostname === 'localhost' ? 'http://localhost:5000' : ''
      );
      const response = await axios.post<GenerateProcessResponse>(
        `${apiUrl}/api/processes/generate`,
        { text, titel },
        { timeout: 300000 } // 5 Minuten Timeout für LLM-Generierung
      );

      // Debug: Log the response
      console.log('API Response:', response.data);
      console.log('Mermaid code:', response.data.mermaid);
      console.log('Mermaid code length:', response.data.mermaid?.length || 0);

      // Validate that we have mermaid code
      if (!response.data.mermaid || response.data.mermaid.trim().length === 0) {
        console.error('No mermaid code in response!');
        setError('Die API hat keinen Mermaid-Code zurückgegeben. Bitte versuchen Sie es erneut.');
        return;
      }

      const newArtefakt: ProcessArtefakt = {
        id: `process-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        titel: response.data.titel,
        mermaid: response.data.mermaid,
        beschreibung: response.data.beschreibung,
        hinweise: response.data.hinweise || [],
        erstelltAm: new Date().toISOString(),
        quelleHash: response.data.quelleHash
      };

      console.log('Created artefakt:', newArtefakt);

      setArtefakte(prev => [newArtefakt, ...prev]);
      setCurrentArtefakt(newArtefakt);
      setText('');
      setTitel('');
    } catch (err: any) {
      console.error('Generation error:', err);

      if (err.response?.status === 429) {
        setError(
          `Rate Limit erreicht. Bitte warten Sie ${err.response.data?.retryAfter || 60} Sekunden und versuchen Sie es erneut.`
        );
      } else if (err.response?.data?.error) {
        setError(err.response.data.error);
      } else if (err.code === 'ECONNABORTED') {
        setError('Timeout: Die Anfrage hat zu lange gedauert. Bitte versuchen Sie es erneut. (Hinweis: Timeout ist auf 5 Minuten eingestellt)');
      } else {
        setError('Fehler beim Generieren des Prozesses. Bitte versuchen Sie es erneut.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = (id: string) => {
    setArtefakte(prev => prev.filter(a => a.id !== id));
    if (currentArtefakt?.id === id) {
      setCurrentArtefakt(null);
    }
  };

  const handleRename = (id: string, newTitel: string) => {
    setArtefakte(prev =>
      prev.map(a => (a.id === id ? { ...a, titel: newTitel } : a))
    );
    if (currentArtefakt?.id === id) {
      setCurrentArtefakt({ ...currentArtefakt, titel: newTitel });
    }
  };

  const handleDuplicate = (id: string) => {
    const original = artefakte.find(a => a.id === id);
    if (original) {
      const duplicate: ProcessArtefakt = {
        ...original,
        id: `process-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        titel: `${original.titel} (Kopie)`,
        erstelltAm: new Date().toISOString()
      };
      setArtefakte(prev => [duplicate, ...prev]);
    }
  };

  const handleView = (artefakt: ProcessArtefakt) => {
    setCurrentArtefakt(artefakt);
    setShowList(false);
  };

  const handleBack = () => {
    setCurrentArtefakt(null);
  };

  const handleSave = (updated: ProcessArtefakt) => {
    // Update in artefakte list
    setArtefakte(prev => prev.map(a => a.id === updated.id ? updated : a));
    // Update current artefakt
    setCurrentArtefakt(updated);
    // Save to localStorage
    const updatedList = artefakte.map(a => a.id === updated.id ? updated : a);
    localStorage.setItem('processArtefakte', JSON.stringify(updatedList));
  };

  const handleCombine = async (selectedIds: string[]) => {
    const selectedArtefakte = artefakte.filter(a => selectedIds.includes(a.id));
    if (selectedArtefakte.length < 2) {
      setError('Bitte wählen Sie mindestens 2 Prozesse aus.');
      return;
    }

    setLoading(true);
    setError(null);
    setShowList(false);

    try {
      const apiUrl = process.env.REACT_APP_API_URL || (
        window.location.hostname === 'localhost' ? 'http://localhost:5000' : ''
      );
      const response = await axios.post<GenerateProcessResponse>(
        `${apiUrl}/api/processes/combine`,
        { processes: selectedArtefakte },
        { timeout: 300000 }
      );

      if (!response.data.mermaid || response.data.mermaid.trim().length === 0) {
        setError('Die API hat keinen Mermaid-Code zurückgegeben.');
        return;
      }

      const newArtefakt: ProcessArtefakt = {
        id: `process-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        titel: response.data.titel,
        mermaid: response.data.mermaid,
        beschreibung: response.data.beschreibung,
        hinweise: response.data.hinweise || [],
        erstelltAm: new Date().toISOString(),
        quelleHash: response.data.quelleHash
      };

      setArtefakte(prev => [newArtefakt, ...prev]);
      setCurrentArtefakt(newArtefakt);
    } catch (err: any) {
      console.error('Combine error:', err);
      if (err.response?.data?.error) {
        setError(err.response.data.error);
      } else if (err.code === 'ECONNABORTED') {
        setError('Timeout: Die Anfrage hat zu lange gedauert.');
      } else {
        setError('Fehler beim Kombinieren der Prozesse.');
      }
    } finally {
      setLoading(false);
    }
  };

  if (currentArtefakt) {
    return (
      <ProcessViewer
        artefakt={currentArtefakt}
        onBack={handleBack}
        onSave={handleSave}
      />
    );
  }

  if (showList) {
    return (
      <ProcessList
        artefakte={artefakte}
        onView={handleView}
        onDelete={handleDelete}
        onRename={handleRename}
        onDuplicate={handleDuplicate}
        onCombine={handleCombine}
        onBack={() => setShowList(false)}
      />
    );
  }

  return (
    <div className="process-generator">
      <div className="process-generator-header">
        <div className="header-main">
          <h2>Prozessgenerator</h2>
        </div>
        <p className="subtitle">
          Geben Sie einen Freitext ein, um eine strukturierte Enterprise-Prozessbeschreibung
          mit Mermaid-Flowchart zu generieren.
        </p>
      </div>

      <div className="process-generator-card">
        <div className="input-group">
          <label htmlFor="titel">Titel (optional)</label>
          <input
            id="titel"
            type="text"
            value={titel}
            onChange={(e) => setTitel(e.target.value)}
            placeholder="z.B. Auszahlung ausgeschiedener Vertriebspartner"
            maxLength={200}
            disabled={loading}
          />
        </div>

        <div className="input-group">
          <label htmlFor="text">
            Prozessbeschreibung (Freitext)
            <span className="char-count">
              {text.length}/{MAX_TEXT_LENGTH}
            </span>
          </label>
          <textarea
            id="text"
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder="Beschreiben Sie hier den Geschäftsprozess in Freitext..."
            rows={12}
            maxLength={MAX_TEXT_LENGTH}
            disabled={loading}
          />
        </div>

        {error && (
          <div className="error-alert">
            <div className="error-icon">⚠</div>
            <div className="error-content">
              <strong>Fehler:</strong> {error}
            </div>
          </div>
        )}

        <div className="button-group">
          <button
            className="btn btn-primary"
            onClick={handleGenerate}
            disabled={loading || text.length < MIN_TEXT_LENGTH}
          >
            {loading ? (
              <>
                <span className="spinner"></span>
                Generiere...
              </>
            ) : (
              'Prozess erzeugen'
            )}
          </button>

          {artefakte.length > 0 && (
            <button
              className="btn btn-secondary"
              onClick={() => setShowList(true)}
              disabled={loading}
            >
              Gespeicherte Prozesse ({artefakte.length})
            </button>
          )}
        </div>
      </div>

      {loading && (
        <div className="loading-info">
          <p>Die Generierung kann bis zu 3 Minuten dauern. Bitte haben Sie Geduld...</p>
        </div>
      )}
    </div>
  );
};

export default ProcessGenerator;
